import type { Call, Gig, Position } from './types.js';
import { calendarDayNumber, shiftDays, startOfDayIn, wallTimeIn } from './timezone.js';

// Pure planning for "change the gig date → move the calls with it". Every
// call (and every position's scopeDate) moves by the same whole number of
// calendar days as the gig date, keeping its wall-clock time in its own
// venue's zone. No IO — the client layer writes the batch, and the existing
// onCallUpdated / onPositionUpdated triggers then refresh people's calendar
// invites and booking snapshots.

export interface GigReschedulePlan {
  // Whole calendar days the gig date moves (negative = earlier). 0 = no-op.
  dayOffset: number;
  gigDate: Date;
  calls: { id: string; startTime: Date; endTime: Date }[];
  positions: { id: string; scopeDate: Date }[];
}

export function planGigReschedule(input: {
  gig: Pick<Gig, 'date' | 'venueId'>;
  calls: Call[];
  positions: Position[];
  // YYYY-MM-DD in the gig's zone (its main venue's).
  newDate: string;
  // IANA zone of a venue by id; `timeZone` is the fallback for a venue that
  // is unset/unknown or has no zone of its own.
  timeZoneFor: (venueId: string | undefined) => string | undefined;
  timeZone: string;
}): GigReschedulePlan {
  const zoneOf = (venueId?: string) => input.timeZoneFor(venueId) ?? input.timeZone;
  const zone = zoneOf(input.gig.venueId);
  const [ny, nm, nd] = input.newDate.split('-').map(Number);
  const dayOffset = calendarDayNumber({ y: ny, m: nm, d: nd }) - calendarDayNumber(wallTimeIn(input.gig.date, zone));
  return {
    dayOffset,
    gigDate: startOfDayIn(input.newDate, zone),
    calls: input.calls.map(c => ({
      id: c.id,
      startTime: shiftDays(c.startTime, dayOffset, c.venueId ? zoneOf(c.venueId) : zone),
      endTime: shiftDays(c.endTime, dayOffset, c.venueId ? zoneOf(c.venueId) : zone),
    })),
    positions: input.positions
      .filter(p => !!p.scopeDate)
      .map(p => ({ id: p.id, scopeDate: shiftDays(p.scopeDate!, dayOffset, zone) })),
  };
}
